import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Checkbox,
  Box,
  IconButton,
  Chip,
  Alert,
  Snackbar
} from "@mui/material";
import { ExpandMore, Add } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { GET, POST } from "APIclient";
import properties from "constants/ApplicationConstants";

export default function AddComponent() {
  const [components, setComponents] = useState([]);
  const [filteredComponents, setFilteredComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState("");
  const [componentName, setComponentName] = useState("");
  const [componentListExpanded, setComponentListExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [open, setOpen] = React.useState(false);
  const [snakColor, setSnakColor] = React.useState("");
  const [snakMessage, setSnakMessage] = React.useState("");

  const handleClose = () => {
    setOpen(false);
  };

  const fetchComponents = async () => {
    try {
      const response = await GET("it-dept/components");

      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setComponents(list);
      setFilteredComponents(list);
    } catch (error) {
      console.error("Error fetching components:", error);
      setComponents([]);
      setFilteredComponents([]);
    }
  };

  useEffect(() => {
    fetchComponents();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredComponents(components);
    } else {
      const filtered = components.filter(component =>
        component.componentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        component.id?.toString().includes(searchTerm)
      );
      setFilteredComponents(filtered);
    }
  }, [searchTerm, components]);

  const handleSave = async () => {
    if (!componentName.trim()) {
      alert("Please enter a component name before saving.");
      return;
    }


    // const exists = components.some(
    //   (comp) =>
    //     comp.componentName?.toLowerCase() === componentName.trim().toLowerCase()
    // );

    // if (exists) {
    //   setSnakColor("info");
    //   setSnakMessage("Component name already exists — skipped.");
    //   setOpen(true);
    // }

    try {
      setLoading(true);
      const response = await POST("it-dept/component/add", { componentName });

      if (response.data?.statusCode === 201 || response.data?.status === "CREATED" || response.data?.success) {
        setSnakColor("success");
        setSnakMessage("Component added successfully");
        setOpen(true);

        setComponentName("");
        setSelectedComponent("");
        await fetchComponents();
      } else {
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Component name already exists.");
      } else if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(error?.message || "Unexpected error occurred.");
      }
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleComponentSelect = (componentId, componentName) => {
    setSelectedComponent(componentId);
    setComponentName(componentName);
    setComponentListExpanded(false);
  };

  const handleClearSelection = () => {
    setSelectedComponent("");
    setComponentName("");
  };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox sx={{ backgroundColor: "#f7f9ff", minHeight: "90vh", py: 1 }}>
        <Paper elevation={6} sx={{ padding: 3, borderRadius: 3 }}>
          <MDTypography variant="h5" sx={{ fontWeight: "bold", mb: 3, fontFamily: properties.fontFamily }}>
            Add Component
          </MDTypography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <MDTypography variant="h6" sx={{ mb: 2, fontFamily: properties.fontFamily }}>
                Available Components
              </MDTypography>

              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search components by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    fontFamily: properties.fontFamily,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px"
                    }
                  }}
                />
                {searchTerm && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ color: "text.secondary" }}
                  >
                    ✕
                  </IconButton>
                )}
              </Box>

              <Accordion
                expanded={componentListExpanded}
                onChange={() => setComponentListExpanded(!componentListExpanded)}
                sx={{
                  boxShadow: "none",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px !important",
                  "&:before": { display: "none" }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    backgroundColor: "#f5f5f5",
                    borderRadius: "8px 8px 0 0",
                    "& .MuiAccordionSummary-content": {
                      margin: "12px 0 !important",
                      fontFamily: properties.fontFamily
                    }
                  }}
                >
                  <MDTypography sx={{ fontFamily: properties.fontFamily, fontSize: "14px" }}>
                    {searchTerm ? `Filtered Components (${filteredComponents.length})` : `View All Components (${components.length})`}
                  </MDTypography>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <List sx={{ maxHeight: "300px", overflow: "auto" }}>
                    {filteredComponents.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary={searchTerm ? "No components found matching your search" : "No components found"}
                          sx={{ fontFamily: properties.fontFamily, textAlign: "center", fontSize: "14px" }}
                        />
                      </ListItem>
                    ) : (
                      filteredComponents.map((component) => (
                        <ListItem key={component.id} sx={{ padding: 0 }}>
                          <ListItemButton
                            onClick={() => handleComponentSelect(component.id, component.componentName)}
                            sx={{
                              fontFamily: properties.fontFamily,
                              backgroundColor: selectedComponent === component.id ? "rgba(0, 0, 0, 0.04)" : "inherit",
                              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" },
                              borderBottom: "1px solid #f0f0f0",
                              py: 0.5,
                              minHeight: "32px"
                            }}
                          >
                            <ListItemText
                              primary={component.componentName || "Unnamed Component"}
                              primaryTypographyProps={{
                                fontFamily: properties.fontFamily,
                                fontSize: "14px",
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} md={6}>
              <MDTypography variant="h6" sx={{ mb: 2, fontFamily: properties.fontFamily }}>
                {selectedComponent ? "Edit Component" : "Add New Component"}
              </MDTypography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label="Component Name"
                  value={componentName}
                  onChange={(e) => setComponentName(e.target.value)}
                  sx={{ fontFamily: properties.fontFamily }}
                />

                {selectedComponent && (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip
                      label={`Editing: ${componentName}`}
                      color="info"
                      sx={{ fontFamily: properties.fontFamily }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleClearSelection}
                      sx={{ color: "error.main" }}
                    >
                      ✕
                    </IconButton>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  {selectedComponent && (
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      onClick={handleClearSelection}
                      sx={{ fontFamily: properties.fontFamily }}
                    >
                      Clear Selection
                    </MDButton>
                  )}
                  <MDButton
                    variant="contained"
                    color="info"
                    onClick={handleSave}
                    disabled={loading || !componentName.trim()}
                    sx={{ fontFamily: properties.fontFamily, background: properties.colors.bgcolorbluethem }}
                  >
                    {loading ? "Saving..." : selectedComponent ? "Update" : "Add Component"}
                  </MDButton>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        <Snackbar
          open={open}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            autoHideDuration={1200}
            onClose={handleClose}
            severity={snakColor}
            variant="filled"
            sx={{ width: "100%", color: "#fff" }}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
